/**
 * DivergingStackedRating — the R8 default exhibit for the `diverging_stacked` plot id.
 *
 * One 100% diverging stacked row per item, CENTERED on the neutral boundary
 * (Bottom-2 | Neutral | Top-2 for odd scales; bottom-half | top-half with no neutral
 * band for even scales), with metrics as ALWAYS-VISIBLE row stats ("28% top-2 ·
 * avg 2.6" — top-2 is the hero, the mean never gets a % sign). Colors are the
 * fixed diverging tokens — a bad->great scale (red pole / light-green midpoint /
 * green pole) — and follow SCALE POLARITY, not scale direction: when `higher_is_better`
 * is false the favorable color moves to the low pole.
 *
 * The card's ONE control is the shared Compare-segments picker: selected cuts derive
 * CLIENT-SIDE from raw responses (per item × per cut) and render as indented rows
 * under each item's always-present "Everyone" anchor row. Honesty rails: cuts n<15
 * are marked directional, n<5 are suppressed from the plot and named in the warning
 * line. NO sig markers on cut rows (policy) — the caption renders only python's
 * precomputed page-vs-page claims.
 *
 * Single-scalar ratings keep the existing summary exhibit (T2B hero + mean +
 * sentiment bar) — passed in as `scalarExhibit` — wrapped in the same chrome, with
 * the picker adding diverging cut rows beneath it.
 */
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { ChartSkeleton, DivergingBar, PlotDotLegend, type DivergingBarSegment } from '@digital-twins/deck-ui/components'
import { buildCardFootnote, QsCardChrome } from './QsCardChrome'
import { useQuickStudyNoun, withSegment } from './quickStudyNoun'
import { CompareSegmentsPicker, type CompareSegmentsGroup } from './CompareSegmentsPicker'
import {
  CUT_DIRECTIONAL_N,
  CUT_SUPPRESS_N,
  buildDemographicCutGroups,
  deriveCutRow,
  distributionFromValues,
  divergingBands,
  divergingLayout,
  divergingRowFromDistribution,
  formatRowStats,
  itemSignificanceClaims,
  itemsAttributable,
  maxCutsFor,
  ratingValueForItem,
  scorecardCellStats,
  type CutRow,
  type DivergingBands,
  type DivergingRow,
} from './divergingStacked'
import { qualifiedCutDisplayLabel } from './audience-segments'
import { honestyThreadsSuffix, sliceResponsesByCutIds, stripLabelQuotes } from './quickStudyPure'
import { CutToggle, orderedSegBands, SegmentSwap, SuppressedCutsNote, useActiveBand } from './segmentedToggle'
import { useQuickStudyCutPlots } from './quickStudyCutPlots'
import { CutPlotsError } from './CutPlotsError'
import type { Result, SurveyQuestion, TwinResponse } from './quickStudyTypes'

// ── QS diverging palette (scorecard cells + Likert bars + legend) ────────────
// ONE shared bad→great ramp for every QS diverging exhibit: a TRUE-neutral gray anchor
// (the old amber --chart-diverging-neutral collapsed near-midpoint values into mud) with
// teal (favorable) / coral (unfavorable) poles. Grays derive from the theme-aware chat
// surface + foreground, so fills stay light in light mode and dark in dark mode while the
// text on top keeps its --twin-chat-fg contrast in both.
export const SC_POSITIVE = '#1d9e75'
export const SC_NEGATIVE = '#d85a30'
// Near-surface tint anchoring the scorecard's mean CELLS (text sits on top of it).
export const SC_NEUTRAL = 'color-mix(in oklab, hsl(var(--twin-chat-fg)) 6%, hsl(var(--twin-chat-surface)))'
// Stronger gray for the diverging bars'/legend's neutral BAND — a solid segment, so it needs
// more presence than SC_NEUTRAL while still receding behind the teal/coral poles in both themes.
export const SC_NEUTRAL_BAND = 'color-mix(in oklab, hsl(var(--twin-chat-fg)) 22%, hsl(var(--twin-chat-surface)))'
// A mean's distance from the midpoint is normalized by only HALF the scale half-range
// (SC_CONTRAST) before it maps to tint intensity, so the compressed band where real means
// live (≈ midpoint ± 1 on a 1–5 scale) uses the whole ramp instead of a sliver of it.
// Scale-agnostic: the window scales with the scale's own range (±1 at 1–5, ±2.25 at 1–10).
const SC_CONTRAST = 0.5
// Peak tint (%) a full-deviation cell mixes toward its pole over the neutral gray — capped
// below 100 so the mean text stays legible on the strongest cell in both light and dark.
export const SC_MAX_TINT = 62

// ── Chrome convergence (Wave 4) ──────────────────────────────────────────────
// These exhibits render their OWN card chrome by default (`chrome='self'` — the
// report/preview surface, byte-identical to before). The chat `InsightCard` passes
// `chrome='none'`: the exhibit then renders BARE (no `QsCardChrome`, no internal
// picker) and PUBLISHES its title-adjacent chrome (scale anchors → subtitle, the
// R10 base footnote, and the Compare-segments picker DATA) UP via `onChrome` so the
// ONE outer `PlotCard` owns them. Cut STATE stays inside the exhibit (the plan's
// sanctioned fallback — the attributability gate, per-item cut model, Mode-B toggle
// and readability cap are all exhibit-specific); the header picker is rendered from
// the published `onChange`/`selected`, so a click round-trips back into this state.
export interface DivergingChromePicker {
  groups: Array<CompareSegmentsGroup>
  selected: Array<string>
  maxCuts: number
  onChange: (selected: Array<string>) => void
}
export interface DivergingChromeData {
  subtitle?: string
  footnote?: string
  picker: DivergingChromePicker | null
}

/** Publish the exhibit's chrome up to `InsightCard` when `enabled` (chrome='none').
 *  A signature guard makes the publish idempotent per meaningful change (avoids a
 *  child→parent setState loop when unmemoized props churn the object identity), and
 *  a `useLayoutEffect` lands the data BEFORE paint so the header never flashes empty.
 *  Cleans up (`onChrome(null)`) on unmount so a torn-down card clears the slots. */
function useChromePublish(
  enabled: boolean,
  onChrome: ((chrome: DivergingChromeData | null) => void) | undefined,
  payload: DivergingChromeData,
  signature: string,
) {
  const payloadRef = useRef(payload)
  payloadRef.current = payload
  const lastSig = useRef<string | null>(null)
  useLayoutEffect(() => {
    if (!enabled || !onChrome) return
    if (lastSig.current === signature) return
    lastSig.current = signature
    onChrome(payloadRef.current)
  }, [enabled, onChrome, signature])
  useLayoutEffect(() => {
    if (!enabled || !onChrome) return
    return () => {
      lastSig.current = null
      onChrome(null)
    }
  }, [enabled, onChrome])
}

/** Legend/segment labels expressed as the ACTUAL scale-value ranges each band covers
 *  (e.g. "1–2 · 3 · 4–5" for a 1–5 scale) rather than the "Bottom-2/Neutral/Top-2" MRX
 *  jargon — so the reader sees which ratings map to which band. A single value collapses
 *  to just the number ("3" not "3–3"). */
function rangeLabel(lo: number, hi: number): string {
  return lo === hi ? String(lo) : `${lo}–${hi}`
}
function bandLabels(bands: DivergingBands): { low: string; mid: string; high: string } {
  return bands.hasNeutral
    ? {
        low: rangeLabel(bands.scaleMin, bands.lowMax),
        mid: rangeLabel(bands.lowMax + 1, bands.highMin - 1),
        high: rangeLabel(bands.highMin, bands.scaleMax),
      }
    : {
        low: rangeLabel(bands.scaleMin, bands.lowMax),
        mid: '',
        high: rangeLabel(bands.highMin, bands.scaleMax),
      }
}

/** Split a server-minted comparison label ("A — Run your restaurant …") into a short BADGE
 *  token (the version tag) and the message text, so the tag can render in a chip and the copy
 *  on its own line. Returns badge=null when there's no short "{tag} — {text}" prefix (e.g.
 *  "Everyone", or a label with no dash) — the caller then renders the label verbatim. Format-
 *  driven (reads the generic tag+dash SHAPE), never any survey's content. */
export function splitComparisonLabel(label: string): { badge: string | null; text: string } {
  // The minter wraps stimulus labels in literal quotes; strip them or the badge regex never
  // matches and the raw '\"A — Depth\"' renders verbatim (the live dt.dev leak).
  const clean = stripLabelQuotes(label)
  const match = /^([A-Za-z0-9]{1,3})\s+[—–-]\s+(.+)$/.exec(clean.trim())
  return match ? { badge: match[1], text: match[2] } : { badge: null, text: clean }
}

/** Colors keyed off POLARITY: the favorable pole is teal (cool-positive), the unfavorable
 *  pole coral — the shared QS diverging palette. Exported so tests can pin the polarity→hue
 *  mapping (the DOM also pins the structural data-good-pole marker). */
export function poleColors(higherIsBetter: boolean): { low: string; high: string } {
  return higherIsBetter
    ? { low: SC_NEGATIVE, high: SC_POSITIVE }
    : { low: SC_POSITIVE, high: SC_NEGATIVE }
}

// ── One centered diverging track row ─────────────────────────────────────────
function DivergingTrackRow({
  label,
  row,
  bands,
  higherIsBetter,
  stat,
  indent = false,
  directional = false,
  testId,
}: {
  label: string
  row: DivergingRow
  bands: DivergingBands
  higherIsBetter: boolean
  /** Right-aligned row stat ("28% top-2 · avg 2.6"); hero weight on the T2B half. */
  stat: string
  indent?: boolean
  directional?: boolean
  testId?: string
}) {
  const { noun: respondents } = useQuickStudyNoun()
  const isRespondents = respondents === 'respondents'
  const seg = divergingLayout(row)
  const colors = poleColors(higherIsBetter)
  const labels = bandLabels(bands)
  const [statHero, ...statRest] = stat.split(' · ')
  // Map the existing 3-band layout onto DivergingBar's per-segment model. Cumulative
  // lefts preserve the exact geometry; DivergingBar carves the gap symmetrically so the
  // neutral midpoint (and thus the shared zero line) stays on 50%.
  const lowLeft = seg.spacerPct
  const neutralLeft = lowLeft + seg.lowPct
  const highLeft = neutralLeft + seg.neutralPct
  const barSegments: Array<DivergingBarSegment> = [
    { leftPct: lowLeft, widthPct: seg.lowPct, color: colors.low, value: `${row.lowPct}%`, label: labels.low },
    ...(bands.hasNeutral
      ? [{ leftPct: neutralLeft, widthPct: seg.neutralPct, color: SC_NEUTRAL_BAND, value: `${row.neutralPct}%`, label: labels.mid }]
      : []),
    { leftPct: highLeft, widthPct: seg.highPct, color: colors.high, value: `${row.highPct}%`, label: labels.high },
  ]
  const directionalMark = directional ? (
    <span
      className="ml-1 text-amber-500"
      title={isRespondents ? 'Small base: a few respondents can swing this row' : 'Small base — a few twins can swing this row'}
      data-testid="qs-diverging-directional"
    >
      ~
    </span>
  ) : null
  const labelTitle = directional
    ? isRespondents
      ? `${label}: small base, a few respondents can swing this row`
      : `${label} — small base, a few twins can swing this row`
    : label
  const bar = (
    <DivergingBar
      segments={barSegments}
      ariaLabel={`${label}: ${row.highPct}% ${labels.high.toLowerCase()}, ${bands.hasNeutral ? `${row.neutralPct}% neutral, ` : ''}${row.lowPct}% ${labels.low.toLowerCase()}`}
      zeroLineClassName="bg-twin-chat-fg/30"
    />
  )
  const statInner = (
    <>
      <span className="font-semibold text-twin-chat-fg">{statHero}</span>
      {statRest.length > 0 && <span className="text-twin-chat-fg-muted"> · {statRest.join(' · ')}</span>}
    </>
  )

  // Cut/segment sub-rows (indented) stay compact — label in a narrow left column so several
  // segments stacked under one item don't balloon the card height.
  if (indent) {
    return (
      <div
        className="flex items-center gap-2 pl-5"
        data-testid={testId ?? 'qs-diverging-row'}
        // Structural polarity marker (QA #19): which pole carries the favorable color.
        data-good-pole={higherIsBetter ? 'high' : 'low'}
      >
        <span className="w-28 shrink-0 truncate text-xs font-normal text-twin-chat-fg" title={labelTitle}>
          {label}
          {directionalMark}
        </span>
        {bar}
        <span className="w-32 shrink-0 text-right text-xs tabular-nums" data-testid="qs-diverging-row-stat">
          {statInner}
        </span>
      </div>
    )
  }

  // Item rows (versions/creatives/pages): the label gets its OWN full-width line above the bar
  // (the version tag chipped into a badge) so long message copy reads in full instead of
  // truncating into a narrow left column. The stat stacks — big top-2 %, then unit, then mean.
  const { badge, text } = splitComparisonLabel(label)
  const [heroPct, ...heroUnit] = statHero.split(' ')
  return (
    <div
      className="flex flex-col gap-1.5"
      data-testid={testId ?? 'qs-diverging-row'}
      // Structural polarity marker (QA #19): which pole carries the favorable color.
      data-good-pole={higherIsBetter ? 'high' : 'low'}
    >
      <div className="flex items-center gap-2">
        {badge ? (
          <span
            aria-hidden
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-twin-chat-fg/15 text-[11px] font-medium text-twin-chat-fg-muted"
          >
            {badge}
          </span>
        ) : null}
        <span className="min-w-0 text-sm font-medium text-twin-chat-fg" title={labelTitle}>
          {badge ? text : label}
          {directionalMark}
        </span>
      </div>
      <div className="flex items-center gap-4">
        {bar}
        <span
          className="shrink-0 text-right leading-tight tabular-nums"
          data-testid="qs-diverging-row-stat"
        >
          <span className="block text-base font-semibold text-twin-chat-fg">{heroPct}</span>
          {heroUnit.length > 0 && <span className="block text-[11px] text-twin-chat-fg-muted">{heroUnit.join(' ')}</span>}
          {statRest.length > 0 && <span className="block text-xs text-twin-chat-fg-muted">{statRest.join(' · ')}</span>}
        </span>
      </div>
    </div>
  )
}

function DivergingLegend({ bands, higherIsBetter }: { bands: DivergingBands; higherIsBetter: boolean }) {
  const labels = bandLabels(bands)
  const colors = poleColors(higherIsBetter)
  // Colored DOTS (reference-clean deviation from the square marks spec) via the shared
  // deck-ui PlotDotLegend; the container testid + muted text color are preserved.
  const items = [
    { color: colors.low, label: labels.low },
    ...(bands.hasNeutral ? [{ color: SC_NEUTRAL_BAND, label: labels.mid }] : []),
    { color: colors.high, label: labels.high },
  ]
  return <PlotDotLegend items={items} className="text-xs text-twin-chat-fg-muted" data-testid="qs-diverging-legend" />
}

// ── The full card ────────────────────────────────────────────────────────────
interface RatingItemView {
  id: string
  label: string
  n: number
  row: DivergingRow | null
  stat: string
}

interface DivergingStackedRatingProps {
  result: Result
  /** The (reconciled) SurveyJS question — anchors ride on minRateDescription/maxRateDescription. */
  question: SurveyQuestion
  /** F1a — suppresses consumer-demographic cut groups on a B2B audience. */
  audienceType?: string
  /** Single-scalar mode: the EXISTING summary exhibit (T2B hero + mean + sentiment
   *  bar) renders as the exhibit; items/cut rows derive from raw responses. */
  scalarExhibit?: React.ReactNode
  /** Wave 2 seam: authored "Audience segments" groups PREPEND ahead of the derived
   *  demographic groups — same shape, no API change. */
  extraSegmentGroups?: Array<CompareSegmentsGroup>
  /** Wave 4 — `'self'` (default) renders the internal `QsCardChrome` exactly as
   *  before (report/preview surface); `'none'` renders the bare exhibit and publishes
   *  its chrome up via `onChrome` so the chat `PlotCard` owns it. */
  chrome?: 'self' | 'none'
  /** Called (under `chrome='none'`) with the exhibit's subtitle/footnote/picker so
   *  `InsightCard` can feed the outer `PlotCard` slots; `null` on unmount. */
  onChrome?: (chrome: DivergingChromeData | null) => void
  /** W6-QS: the read as Python's stamped `diverging-likert` plot (the scalar summary, or the
   *  per-item rows). Drawn in place of the browser exhibit while no cut is selected; a selected
   *  cut draws only the legacy cut view. */
  plotExhibit?: React.ReactNode
  /** W6-QS: names the box the significance test reads when the stamped plot draws another
   *  Top N (Top 3 on a 10 or 11 point scale). Shown with the claims, on the stamped view only. */
  testedBoxNote?: string
  /**
   * W7 Q11: draws a read's stamped likert plot (the whole run, or one cut band from the
   * cut-plots endpoint), or null when the read carries none. Given and drawable, a picked
   * cut asks the endpoint (the scorecard's hook and rules) and each band is its own stamped
   * plot; a failure or refusal shows an error with a retry. A stored result keeps the
   * browser cut rows.
   */
  drawPlot?: (read: Result) => ReactNode | null
}

export function DivergingStackedRating({ result: r, question, audienceType, scalarExhibit, extraSegmentGroups, chrome = 'self', onChrome, plotExhibit, testedBoxNote, drawPlot }: DivergingStackedRatingProps) {
  const { noun: respondents, segmentLabel } = useQuickStudyNoun()
  const isRespondents = respondents === 'respondents'
  const responses = useMemo(() => r.responses ?? [], [r.responses])
  const higherIsBetter = r.higher_is_better !== false
  const qName = question?.name ?? 'qs_main'
  const isScalar = Boolean(scalarExhibit)

  // Scale bounds: python per-item stats are authoritative for monadic; the question
  // carries them for the scalar case.
  const monadicItems = !isScalar && r.monadic && r.monadic_rating && r.monadic_rating.length > 0 ? r.monadic_rating : null
  const scaleMin = monadicItems ? (monadicItems[0]?.scale_min ?? 1) : (typeof question?.rateMin === 'number' ? question.rateMin : 1)
  const scaleMax = monadicItems ? (monadicItems[0]?.scale_max ?? 5) : (typeof question?.rateMax === 'number' ? question.rateMax : 5)
  const bands = divergingBands(scaleMin, scaleMax)

  // Item views. Monadic: python's per-item stats are the source (mean/top-2 stats
  // stay server-authoritative); the row geometry derives from the shipped
  // distribution. Scalar: one "Everyone" pseudo-item derived from raw responses.
  const items = useMemo<Array<RatingItemView>>(() => {
    if (monadicItems) {
      return monadicItems.map((it) => ({
        id: String(it.id),
        label: it.label,
        n: it.n,
        row: divergingRowFromDistribution(it.distribution, scaleMin, scaleMax),
        stat: formatRowStats(it.top_2_box, it.mean),
      }))
    }
    const values: Array<number> = []
    for (const resp of responses) {
      const v = ratingValueForItem(resp?.survey_data, qName, '__all__', true)
      if (v != null) values.push(v)
    }
    const row = divergingRowFromDistribution(distributionFromValues(values, scaleMin, scaleMax), scaleMin, scaleMax)
    if (!row) return []
    return [{ id: '__all__', label: 'Everyone', n: row.n, row, stat: formatRowStats(row.top2Pct, row.mean) }]
  }, [monadicItems, responses, qName, scaleMin, scaleMax])

  // ── Compare segments (the card's ONE control) ──────────────────────────────
  const [selectedCuts, setSelectedCuts] = useState<Array<string>>([])
  const maxCuts = maxCutsFor(isScalar ? 1 : items.length)
  const acceptScalar = isScalar || items.length === 1
  // Client-side cuts need item-attributable responses. A stored multi-item run whose
  // pooled responses are plain scalars can't be sliced (no item identity on the row)
  // → the picker hides entirely rather than shipping a dead control. The python
  // item-keyed response contract (`survey_data.qs_main = { itemId: value }`) unlocks
  // it for new runs with zero FE change.
  const attributable = useMemo(
    () => itemsAttributable(responses, qName, items.map((i) => i.id)),
    [responses, qName, items],
  )
  const cutGroups = useMemo<Array<CompareSegmentsGroup>>(
    () => [...(extraSegmentGroups ?? []), ...buildDemographicCutGroups(responses, audienceType)],
    [extraSegmentGroups, responses, audienceType],
  )
  const showPicker = attributable && cutGroups.some((g) => g.options.length > 0)

  // W7 Q11: a stamped read's picked cuts are Python's (the cut-plots endpoint), one band each.
  const isStamped = Boolean(drawPlot && drawPlot(r) !== null)
  const cutLabelFor = useMemo(() => {
    const map = new Map<string, string>()
    for (const g of cutGroups) for (const o of g.options) map.set(o.id, qualifiedCutDisplayLabel(g.groupLabel, o.label))
    return (cid: string): string | undefined => map.get(cid)
  }, [cutGroups])
  const cutSlices = useMemo(
    () => (isStamped ? sliceResponsesByCutIds(responses, selectedCuts, cutLabelFor) : []),
    [isStamped, responses, selectedCuts, cutLabelFor],
  )
  const stampedBands = useMemo(() => orderedSegBands(responses, cutSlices), [responses, cutSlices])
  const [activeBand, setActiveBand] = useActiveBand(stampedBands)
  const cutPlotRequest = useMemo(
    () => cutSlices.map((slice) => ({ id: slice.cutId, label: slice.label })),
    [cutSlices],
  )
  const cutPlots = useQuickStudyCutPlots(r, isStamped ? cutPlotRequest : NO_CUT_PLOT_REQUEST)
  // A stored read outside a thread keeps the browser rows; a new read (`plot_inputs`) never does (W7 M1).
  const drawsStampedCuts =
    isStamped && selectedCuts.length > 0 && (cutPlots.status !== 'unavailable' || Boolean(r.plot_inputs))

  // Per item × per selected cut (memoized off the raw inputs).
  const cutRowsByItem = useMemo<Map<string, Array<CutRow>>>(() => {
    const map = new Map<string, Array<CutRow>>()
    if (!selectedCuts.length || drawsStampedCuts) return map
    for (const item of items) {
      map.set(
        item.id,
        selectedCuts.map((cid) => deriveCutRow(responses, qName, item.id, acceptScalar, cid, scaleMin, scaleMax)),
      )
    }
    return map
  }, [selectedCuts, items, responses, qName, acceptScalar, scaleMin, scaleMax, drawsStampedCuts])

  // Honesty rails: suppressed cuts (n < CUT_SUPPRESS_N) are named, never plotted;
  // directional cuts (n < 15) carry their mark on the row itself.
  const suppressedNotes = useMemo(() => {
    const seen = new Map<string, number>()
    for (const rows of cutRowsByItem.values()) {
      for (const c of rows) {
        if (c.suppressed) seen.set(c.label, Math.min(seen.get(c.label) ?? Infinity, c.n))
      }
    }
    return [...seen.entries()].map(([label, n]) => `${label} (n=${n})`)
  }, [cutRowsByItem])
  const hasDirectional = useMemo(
    () => [...cutRowsByItem.values()].some((rows) => rows.some((c) => c.directional && !c.suppressed)),
    [cutRowsByItem],
  )

  // ── Chrome content ──────────────────────────────────────────────────────────
  const minLabel = String(question?.minRateDescription ?? '').trim()
  const maxLabel = String(question?.maxRateDescription ?? '').trim()
  const anchorsLine =
    minLabel || maxLabel
      ? `${scaleMin} = ${minLabel || scaleMin} · ${scaleMax} = ${maxLabel || scaleMax}`
      : `Scale ${scaleMin}–${scaleMax}`
  const noun = r.item_noun ?? 'creative'
  const nPer = r.n_per_creative ?? (items.length ? Math.round((r.total || 0) / items.length) || undefined : undefined)
  // R10 — ONE muted base-size line; the isolation caveat folds into the base phrase.
  // True-monadic Step 5 honesty suffix — '' unless a twin-monadic reuse fallback fired.
  const honestySuffix = honestyThreadsSuffix(r.n_unique_respondents, r.n_reused_threads, respondents)
  const footnote = monadicItems
    ? buildCardFootnote(
        withSegment(nPer != null ? `${nPer} ${respondents} per ${noun}` : `${items.length} ${noun}s`, segmentLabel) + honestySuffix,
      )
    : items[0]
      ? // W0.5 — a monadic result must NEVER read as a pooled base ("Base: 200 twins" under
        // per-version bars overstates each bar's n). Even when the items arrived through the
        // scalar path, phrase the base per version when the run was monadic.
        buildCardFootnote(
          withSegment(r.monadic ? `${items[0].n} ${respondents} per ${noun}` : `${items[0].n} ${respondents}`, segmentLabel),
        )
      : undefined
  const claims = itemSignificanceClaims(r.significance)

  // Picker DATA (built once): rendered inline under `chrome='self'`, published up under
  // `chrome='none'` so the outer PlotCard header renders the same control.
  const pickerData: DivergingChromePicker | null = showPicker
    ? { groups: cutGroups, selected: selectedCuts, maxCuts, onChange: setSelectedCuts }
    : null
  // Publish anchors/footnote/picker to InsightCard when this is the chrome-less chat path.
  const chromeValid = Boolean(bands) && (items.length > 0 || isScalar)
  useChromePublish(
    chrome === 'none' && chromeValid,
    onChrome,
    { subtitle: anchorsLine, footnote, picker: pickerData },
    `${anchorsLine}|${footnote ?? ''}|${showPicker ? '1' : '0'}|${selectedCuts.join(',')}|${maxCuts}|${cutGroups.length}`,
  )

  if (!bands || (!items.length && !isScalar)) {
    return null
  }

  const renderCutRows = (itemId: string) => {
    const rows = cutRowsByItem.get(itemId) ?? []
    return rows
      .filter((c) => !c.suppressed && c.row)
      .map((c) => (
        <DivergingTrackRow
          key={c.cutId}
          label={`${c.label} (n=${c.n})`}
          row={c.row!}
          bands={bands}
          higherIsBetter={higherIsBetter}
          stat={formatRowStats(c.row!.top2Pct, c.row!.mean)}
          indent
          directional={c.directional}
          testId="qs-diverging-cut-row"
        />
      ))
  }

  const drawsPlot = Boolean(plotExhibit) && selectedCuts.length === 0
  const showLegend = (!isScalar || selectedCuts.length > 0) && !drawsPlot

  const captionEl =
    claims.length > 0 ? (
      <div className="space-y-0.5" data-testid="qs-diverging-sig-claims">
        {claims.map((claim) => (
          <p key={claim} className="text-xs text-twin-chat-fg">
            <span aria-hidden className="mr-1 text-twin-chat-send">✓</span>
            {claim}
          </p>
        ))}
        {drawsPlot && testedBoxNote && (
          <p className="text-[11px] text-twin-chat-fg-muted" data-testid="qs-diverging-sig-box-note">
            {testedBoxNote}
          </p>
        )}
      </div>
    ) : undefined

  // W7 Q11: the stamped likert per picked band once the endpoint answered for every drawn band
  // (a suppressed pick is named, never drawn); loading draws the skeleton, anything else the error.
  const stampedCutBody = ((): ReactNode | null => {
    if (!drawsStampedCuts || !drawPlot) {
      return null
    }
    if (cutPlots.status === 'loading') {
      return (
        <div data-testid="qs-diverging-cut-plots-loading">
          <ChartSkeleton />
        </div>
      )
    }
    const answered = cutPlots.question
    const bandPlots = answered?.status === 'ok' ? answered.plots ?? {} : {}
    // A cut band reads only its own plot: the whole run's is cleared first.
    const readFor = (cutId: string): Result =>
      cutId === '__all__' ? r : { ...r, diverging_likert_plot: null, ...(bandPlots[`cut:${cutId}`] ?? {}) }
    const drawnBands = stampedBands.filter((band) => !band.suppressed)
    const isDrawable =
      cutPlots.status === 'ready' &&
      answered?.status === 'ok' &&
      drawnBands.every(
        (band) => band.cutId === '__all__' || (bandPlots[`cut:${band.cutId}`] != null && drawPlot(readFor(band.cutId)) !== null),
      )
    if (!isDrawable) {
      return <CutPlotsError onRetry={cutPlots.retry} />
    }
    const shown = drawnBands.find((band) => band.cutId === activeBand) ?? drawnBands[0]
    return (
      <div className="space-y-3" data-testid="qs-diverging-cut-plots">
        {drawnBands.length > 1 && <CutToggle bands={drawnBands} active={shown.cutId} onSelect={setActiveBand} />}
        <SegmentSwap swapKey={shown.cutId}>{drawPlot(readFor(shown.cutId))}</SegmentSwap>
        <SuppressedCutsNote labels={stampedBands.filter((band) => band.suppressed).map((band) => band.label)} />
      </div>
    )
  })()

  const body = stampedCutBody ? (
      <div className="space-y-6" data-testid="qs-diverging-stacked">
        {stampedCutBody}
      </div>
  ) : (
      <div className="space-y-6" data-testid="qs-diverging-stacked">
        {isScalar && (drawsPlot ? plotExhibit : scalarExhibit)}
        {isScalar && selectedCuts.length > 0 && items[0]?.row && (
          <div className="space-y-2">
            <DivergingTrackRow
              label="Everyone"
              row={items[0].row}
              bands={bands}
              higherIsBetter={higherIsBetter}
              stat={items[0].stat}
              testId="qs-diverging-everyone-row"
            />
            {renderCutRows(items[0].id)}
          </div>
        )}
        {!isScalar && drawsPlot && plotExhibit}
        {!isScalar &&
          !drawsPlot &&
          items.map((item) => {
            const withCuts = selectedCuts.length > 0
            return (
              <div key={item.id} className="space-y-2" data-testid="qs-diverging-item">
                {withCuts ? (
                  <>
                    <p className="text-xs font-medium text-twin-chat-fg" title={item.label}>
                      {item.label} <span className="font-normal text-twin-chat-fg-muted">(n={item.n})</span>
                    </p>
                    {item.row && (
                      <DivergingTrackRow
                        label="Everyone"
                        row={item.row}
                        bands={bands}
                        higherIsBetter={higherIsBetter}
                        stat={item.stat}
                        testId="qs-diverging-everyone-row"
                      />
                    )}
                    {renderCutRows(item.id)}
                  </>
                ) : (
                  item.row && (
                    <DivergingTrackRow
                      label={item.label}
                      row={item.row}
                      bands={bands}
                      higherIsBetter={higherIsBetter}
                      stat={item.stat}
                      testId="qs-diverging-item-row"
                    />
                  )
                )}
              </div>
            )
          })}
        {hasDirectional && (
          <p className="text-[11px] text-amber-500" data-testid="qs-diverging-directional-note">
            {isRespondents
              ? `⚠ Cuts under n=${CUT_DIRECTIONAL_N} can swing on a few respondents. Confirm before acting on them.`
              : `⚠ Cuts under n=${CUT_DIRECTIONAL_N} can swing on a few twins — confirm before acting on them.`}
          </p>
        )}
        {suppressedNotes.length > 0 && (
          <p className="text-[11px] text-amber-500" data-testid="qs-diverging-suppressed-note">
            ⚠ Too few {respondents} to plot: {suppressedNotes.join(', ')} (below the n={CUT_SUPPRESS_N} floor).
          </p>
        )}
        {showLegend && <DivergingLegend bands={bands} higherIsBetter={higherIsBetter} />}
      </div>
  )

  // Chat surface — bare exhibit; the outer PlotCard renders the published chrome.
  if (chrome === 'none') {
    return (
      <>
        {body}
        {captionEl}
      </>
    )
  }

  // Report/preview surface — the original internal chrome, byte-identical.
  return (
    <QsCardChrome
      construct={r.title}
      anchorsLine={anchorsLine}
      control={
        pickerData ? (
          <CompareSegmentsPicker groups={cutGroups} selected={selectedCuts} onChange={setSelectedCuts} maxCuts={maxCuts} />
        ) : undefined
      }
      caption={captionEl}
      footnote={footnote}
    >
      {body}
    </QsCardChrome>
  )
}

// ── Scorecard matrix: one diverging row-group PER DIMENSION ──────────────────
// The LP scorecard grid (Step 6 / R2): dimensions-as-groups × items-as-rows. Each
// dimension is a header + one diverging row per item, reusing the SAME band math,
// polarity palette, and metric-aware row stats as the single-metric card — only the
// SHAPE differs (grouped by dimension). Monadic isolation is preserved server-side:
// each item's cells come only from that item's isolated pass.
type ScorecardDimension = NonNullable<Result['monadic_scorecard']>[number]

interface DivergingScorecardProps {
  result: Result
  question: SurveyQuestion
  /** F1a — suppresses consumer-demographic cut groups on a B2B audience (scorecard picker). */
  audienceType?: string
  /** Authored "Audience segments" groups PREPEND ahead of the derived demographic groups. */
  extraSegmentGroups?: Array<CompareSegmentsGroup>
  /** Wave 4 — `'self'` (default) keeps the internal `QsCardChrome`; `'none'` renders the
   *  bare heatmap and publishes its footnote/picker up via `onChrome` (chat PlotCard). */
  chrome?: 'self' | 'none'
  onChrome?: (chrome: DivergingChromeData | null) => void
  /**
   * SC: draws a read's stamped scorecard (the shared heat grid, `scorecard_plot`), or null
   * when the read carries none. Given and drawable, the exhibit is the stamped grid with no
   * cut picked, and each picked cut's band is its own stamped grid from the cut-plots
   * endpoint (the other cards' hook and fallback rules). A stored result, a refusal or a
   * failure keeps this component's legacy table and its browser cut recompute.
   */
  drawPlot?: (read: Result) => ReactNode | null
}

/** No cuts asked (a stored scorecard never asks the cut-plots endpoint). */
const NO_CUT_PLOT_REQUEST: Array<{ id: string; label?: string }> = []

// ── Scorecard HEATMAP: dimensions × items, mean-anchored diverging cells ─────
// The LP scorecard exhibit for a monadic matrixdropdown grid. Rows = dimensions,
// columns = items (pages A, B). Each cell prints the MEAN in a text token and paints
// its background on a diverging ramp ANCHORED AT THE SCALE MIDPOINT — scale-agnostic
// (works for 1–5 midpoint 3 AND 1–10 midpoint 5.5) because the mean's deviation is
// normalized by the scale half-range before it is mapped to a hue. Favorable pole
// follows `dimension.higher_is_better`. The number never wears the fill color; the
// grid is a real semantic <table> so the "table view" a11y rule holds by construction.

/** Diverging cell tone for a mean, anchored at the scale MIDPOINT and scale-agnostic.
 *  goodness = signed, half-range-normalized distance from the midpoint, flipped when
 *  higher_is_better is false; |goodness| drives how far the fill ramps from the neutral
 *  gray toward the favorable (positive hue) or unfavorable (negative hue) pole. The fill
 *  is tinted toward the card surface at a fixed ratio so the printed mean (a TEXT token,
 *  never the fill) stays legible in BOTH themes. Exported for unit tests. */
export interface ScorecardCellTone {
  polarity: 'positive' | 'negative' | 'neutral'
  /** 0..100 — |goodness| × 100, how far the fill ramps off the neutral midpoint. */
  intensity: number
  /** CSS color-mix background string (jsdom drops it; tests pin polarity/intensity). */
  background: string
}

/** Sequential cell tone for a 0..100 share (theme mention %), ramping a single pole off the
 *  true-neutral gray. Mirror of scorecardCellTone's tint math WITHOUT the midpoint anchor: the
 *  caller picks the pole by row polarity (SC_POSITIVE teal / SC_NEGATIVE coral / SC_NEUTRAL_BAND
 *  for a mixed row). 0% stays plain neutral. The printed % is a TEXT token, never this fill.
 *  Exported for unit tests. */
export interface SequentialCellTone {
  /** 0..100 — the clamped, rounded percent; drives how far the fill ramps off neutral. */
  intensity: number
  /** CSS color-mix background string (jsdom drops it; tests pin intensity). */
  background: string
}

export function sequentialCellTone(percent: number, pole: string): SequentialCellTone {
  const safePercent = Number.isFinite(percent) ? percent : 0
  const intensity = Math.round(Math.max(0, Math.min(100, safePercent)))
  if (intensity === 0) {
    return { intensity, background: SC_NEUTRAL }
  }
  const tintPct = Math.round((intensity / 100) * SC_MAX_TINT)
  return { intensity, background: `color-mix(in oklab, ${pole} ${tintPct}%, ${SC_NEUTRAL})` }
}

export function scorecardCellTone(
  mean: number,
  scaleMin: number,
  scaleMax: number,
  higherIsBetter: boolean,
): ScorecardCellTone {
  const midpoint = (scaleMin + scaleMax) / 2
  const half = (scaleMax - scaleMin) / 2 || 1
  // Normalize by only HALF the half-range (SC_CONTRAST) so the narrow band where real means
  // cluster fills the ramp instead of a sliver of it; clamp to ±1 (means past the window peg
  // to the pole). `intensity` (0..100) stays the published deviation strength for data-intensity.
  const deviation = Math.max(-1, Math.min(1, (mean - midpoint) / (half * SC_CONTRAST)))
  const goodness = higherIsBetter ? deviation : -deviation
  const intensity = Math.round(Math.min(1, Math.abs(goodness)) * 100)
  const polarity: ScorecardCellTone['polarity'] = intensity === 0 ? 'neutral' : goodness > 0 ? 'positive' : 'negative'
  // Diverging ramp anchored on a TRUE-neutral gray: the midpoint stays gray and each cell mixes
  // toward its favorable (teal) / unfavorable (coral) pole in proportion to |goodness|, capped at
  // SC_MAX_TINT so the printed mean (a TEXT token, never the fill) keeps contrast in both themes.
  const pole = polarity === 'positive' ? SC_POSITIVE : polarity === 'negative' ? SC_NEGATIVE : SC_NEUTRAL
  const tintPct = Math.round((intensity / 100) * SC_MAX_TINT)
  const background = polarity === 'neutral' ? SC_NEUTRAL : `color-mix(in oklab, ${pole} ${tintPct}%, ${SC_NEUTRAL})`
  return { polarity, intensity, background }
}

function ScorecardHeatmapLegend({
  scaleMin,
  scaleMax,
  higherIsBetter,
  minLabel,
  maxLabel,
}: {
  scaleMin: number
  scaleMax: number
  higherIsBetter: boolean
  /** Scale-anchor descriptions — what the endpoints MEAN (e.g. "Not at all" / "Extremely"),
   *  read from the question's rating columns. Blank when the scale has no authored anchors,
   *  in which case the legend shows the bare numbers only. */
  minLabel?: string
  maxLabel?: string
}) {
  // Scorecard-local diverging ramp (teal favorable / coral unfavorable), flipped by polarity so
  // the gradient matches the cell fills; anchor labels stay pinned to the NUMBERS, not to good/bad.
  const low = higherIsBetter ? SC_NEGATIVE : SC_POSITIVE
  const high = higherIsBetter ? SC_POSITIVE : SC_NEGATIVE
  const midpoint = (scaleMin + scaleMax) / 2
  const midLabel = Number.isInteger(midpoint) ? String(midpoint) : midpoint.toFixed(1)
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-twin-chat-fg-muted" data-testid="qs-scorecard-heatmap-legend">
      <span className="tabular-nums">{scaleMin}</span>
      {minLabel ? <span data-testid="qs-scorecard-heatmap-anchor-min">{minLabel}</span> : null}
      <span
        aria-hidden
        className="h-2.5 w-16 rounded-sm"
        style={{ background: `linear-gradient(to right, ${low}, ${SC_NEUTRAL}, ${high})` }}
      />
      {maxLabel ? <span data-testid="qs-scorecard-heatmap-anchor-max">{maxLabel}</span> : null}
      <span className="tabular-nums">{scaleMax}</span>
      <span className="ml-1">midpoint {midLabel} = neutral</span>
    </div>
  )
}

/** One heatmap cell's display stats — from ONE math path (python default OR the client
 *  recompute), so a toggled cut view never mixes banker's-rounded and half-up numbers. */
interface HeatCell {
  n: number
  mean: number
  top2Pct: number
  scaleMin: number
  scaleMax: number
}

export function ScorecardHeatmap({ result: r, question, audienceType, extraSegmentGroups, chrome = 'self', onChrome, drawPlot }: DivergingScorecardProps) {
  const { noun: respondents, segmentLabel } = useQuickStudyNoun()
  const isRespondents = respondents === 'respondents'
  const responses = useMemo(() => r.responses ?? [], [r.responses])
  const qName = question?.name ?? 'qs_main'
  const dims = (r.monadic_scorecard ?? []) as Array<ScorecardDimension>
  const noun = r.item_noun ?? 'page'
  const nPer = r.n_per_creative ?? undefined

  const rendered = useMemo(() => {
    return dims
      .map((dim) => ({
        dimension: dim.dimension,
        description: dim.description,
        higherIsBetter: dim.higher_is_better !== false,
        items: (dim.items ?? []).map((it) => ({
          id: String(it.id),
          label: it.label,
          n: it.n,
          mean: it.mean,
          top2: it.top_2_box_pct,
          distribution: it.distribution,
          scaleMin: it.scale_min ?? 1,
          scaleMax: it.scale_max ?? 5,
        })),
      }))
      .filter((d) => d.items.length > 0)
  }, [dims])

  // Each dimension's matrix COLUMN id — the key python aggregates each cell from
  // (survey_data[qName][itemId][colId] in _scorecard_dim_item_stats). python builds
  // monadic_scorecard in question.columns ORDER with dim_label = column.label, so index-align
  // (title-match first for resilience). Lets the client recompute address the RIGHT dimension.
  const dimColIds = useMemo(() => {
    const cols = (question?.columns ?? []) as Array<{ name?: string; value?: string; title?: string }>
    return rendered.map((dim, i) => {
      const byTitle = cols.find((c) => String(c?.title ?? '').trim() === dim.dimension.trim())
      const col = byTitle ?? cols[i]
      return col ? String(col.name ?? col.value ?? '') : ''
    })
  }, [rendered, question])

  // ── Compare segments — the card's ONE control; Mode B toggle (a grid can't stack) ──
  const [selectedCuts, setSelectedCuts] = useState<Array<string>>([])
  const itemIds = useMemo(() => rendered[0]?.items.map((it) => it.id) ?? [], [rendered])
  const scaleMin = rendered[0]?.items[0]?.scaleMin ?? 1
  const scaleMax = rendered[0]?.items[0]?.scaleMax ?? 5
  // Client cuts need item-attributable responses (same gate as the diverging card): a stored
  // run without the item-keyed survey_data can't be sliced, so the picker hides.
  const attributable = useMemo(() => itemsAttributable(responses, qName, itemIds), [responses, qName, itemIds])
  const cutGroups = useMemo<Array<CompareSegmentsGroup>>(
    () => [...(extraSegmentGroups ?? []), ...buildDemographicCutGroups(responses, audienceType)],
    [extraSegmentGroups, responses, audienceType],
  )
  const showPicker = attributable && cutGroups.some((g) => g.options.length > 0)
  const maxCuts = maxCutsFor(itemIds.length || 1)
  const cutLabelFor = useMemo(() => {
    const map = new Map<string, string>()
    for (const g of cutGroups) for (const o of g.options) map.set(o.id, o.label)
    return (cid: string): string | undefined => map.get(cid)
  }, [cutGroups])
  const cutSlices = useMemo(
    () => sliceResponsesByCutIds(responses, selectedCuts, cutLabelFor),
    [responses, selectedCuts, cutLabelFor],
  )
  // Bands = the "Everyone" anchor + each selected cut (ordinal order); the toggle shows one.
  const bands = useMemo(() => orderedSegBands(responses, cutSlices), [responses, cutSlices])
  const [activeBand, setActiveBand] = useActiveBand(bands)
  // SC: a stamped scorecard asks its picked cuts' grids from the cut-plots endpoint.
  const stampedPlot = drawPlot ? drawPlot(r) : null
  const cutPlotRequest = useMemo(
    () => cutSlices.map((slice) => ({ id: slice.cutId, label: slice.label })),
    [cutSlices],
  )
  const cutPlots = useQuickStudyCutPlots(r, stampedPlot ? cutPlotRequest : NO_CUT_PLOT_REQUEST)

  // R10 base footnote (moved above the early return so the chrome-publish hook can read it).
  const footnote = buildCardFootnote(
    withSegment(nPer != null ? `${nPer} ${respondents} per ${noun}` : `${(rendered[0]?.items.length ?? 0)} ${noun}s`, segmentLabel) +
      honestyThreadsSuffix(r.n_unique_respondents, r.n_reused_threads, respondents),
  )
  // Picker DATA (built once): inline under `chrome='self'`, published up under `chrome='none'`.
  const pickerData: DivergingChromePicker | null = showPicker
    ? { groups: cutGroups, selected: selectedCuts, maxCuts, onChange: setSelectedCuts }
    : null
  // Publish footnote/picker to InsightCard on the chrome-less chat path (scorecard has no
  // scale-anchor subtitle — its scale legend renders inside the exhibit body).
  useChromePublish(
    chrome === 'none' && rendered.length > 0,
    onChrome,
    { subtitle: undefined, footnote, picker: pickerData },
    `${footnote ?? ''}|${showPicker ? '1' : '0'}|${selectedCuts.join(',')}|${maxCuts}|${cutGroups.length}`,
  )

  if (!rendered.length) return null

  // Columns = the item set (order + titles-first labels) from the first dimension; every
  // dimension shares the same items (same pages), looked up per row by id. Labels are the
  // server-minted "A — {title}" / "Page A" strings — the FE renders them verbatim.
  const columns = rendered[0].items.map((it) => ({ id: it.id, label: stripLabelQuotes(it.label) }))
  const legendHigher = rendered[0].higherIsBetter
  const hasCuts = cutSlices.length > 0
  // A/B gap column: only meaningful for an exactly-two-item grid (the single "A − B" delta).
  const isPair = columns.length === 2
  // Scale-anchor descriptions — what the endpoints MEAN. For a matrixdropdown scorecard the twins
  // see these on each rating COLUMN (twins.py stamps min/maxRateDescription per column off the
  // shared scale), so read the first column with a matrix-level fallback. Survey-agnostic: only the
  // generic SurveyJS rating-anchor fields are read, never any survey's content.
  const anchorCol = (question?.columns ?? [])[0] as
    | { minRateDescription?: unknown; maxRateDescription?: unknown }
    | undefined
  const minAnchorLabel = String(anchorCol?.minRateDescription ?? question?.minRateDescription ?? '').trim()
  const maxAnchorLabel = String(anchorCol?.maxRateDescription ?? question?.maxRateDescription ?? '').trim()

  // Default (no cut): python's server-authoritative per-cell stats — unchanged.
  const pythonCell = (dimIndex: number, colId: string): HeatCell | null => {
    const item = rendered[dimIndex].items.find((i) => i.id === colId)
    return item && typeof item.mean === 'number'
      ? { n: item.n, mean: item.mean, top2Pct: item.top2, scaleMin: item.scaleMin, scaleMax: item.scaleMax }
      : null
  }
  // Cut mode: recompute EVERY cell (including the Everyone band) client-side from the band's
  // raw responses (survey_data[qName][itemId][dimColId]) so the toggled bands share one math
  // path. Zero backend — the same slice-and-average as the rating charts.
  const recomputeCell = (bandResponses: Array<TwinResponse>) => (dimIndex: number, colId: string): HeatCell | null => {
    const dimColId = dimColIds[dimIndex]
    if (!dimColId) return null
    const s = scorecardCellStats(bandResponses, qName, colId, dimColId, scaleMin, scaleMax)
    return { n: s.n, mean: s.mean, top2Pct: s.top2Pct, scaleMin, scaleMax }
  }

  const renderTable = (cellFor: (dimIndex: number, colId: string) => HeatCell | null) => (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-2 text-xs" role="table">
        <thead>
          <tr>
            <th scope="col" className="w-full text-left align-bottom font-medium text-twin-chat-fg-muted">
              Dimension
            </th>
            {columns.map((col) => (
              <th
                key={col.id}
                scope="col"
                className="w-64 truncate px-1 text-center align-bottom font-medium text-twin-chat-fg"
                title={stripLabelQuotes(col.label)}
                data-testid="qs-scorecard-heatmap-col"
              >
                {stripLabelQuotes(col.label)}
              </th>
            ))}
            {isPair && (
              <th
                scope="col"
                className="w-14 px-1 text-center align-bottom font-medium text-twin-chat-fg-muted"
                title={`Gap: ${stripLabelQuotes(columns[0].label)} − ${stripLabelQuotes(columns[1].label)}`}
                data-testid="qs-scorecard-heatmap-delta-col"
              >
                Δ
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rendered.map((dim, dimIndex) => (
            <tr key={dim.dimension} data-testid="qs-scorecard-heatmap-row">
              <th
                scope="row"
                className="w-full truncate pr-2 text-left align-middle font-normal text-twin-chat-fg"
                title={stripLabelQuotes(dim.description || dim.dimension)}
              >
                {stripLabelQuotes(dim.dimension)}
              </th>
              {columns.map((col) => {
                const cell = cellFor(dimIndex, col.id)
                if (!cell) {
                  return (
                    <td
                      key={col.id}
                      className="h-10 w-64 rounded-md bg-twin-chat-fg/5 text-center align-middle text-twin-chat-fg-muted"
                      data-testid="qs-scorecard-heatmap-cell"
                      data-dim={dim.dimension}
                      data-item={col.id}
                      data-value=""
                      data-polarity="neutral"
                    >
                      –
                    </td>
                  )
                }
                const suppressed = cell.n < CUT_SUPPRESS_N
                const directional = !suppressed && cell.n < CUT_DIRECTIONAL_N
                const tone = scorecardCellTone(cell.mean, cell.scaleMin, cell.scaleMax, dim.higherIsBetter)
                const top2 = Math.round(cell.top2Pct)
                const title = suppressed
                  ? isRespondents
                    ? `${dim.dimension} · ${col.label}: n=${cell.n}, too few respondents to score`
                    : `${dim.dimension} · ${col.label} — n=${cell.n}, too few twins to score`
                  : `${dim.dimension} · ${col.label} — n=${cell.n}${directional ? ' (small base)' : ''} · mean ${cell.mean.toFixed(1)} · ${top2}% top-2`
                return (
                  <td
                    key={col.id}
                    className={cn(
                      'h-10 w-64 rounded-md text-center align-middle tabular-nums',
                      suppressed && 'bg-twin-chat-fg/5',
                      directional && 'opacity-60',
                    )}
                    style={suppressed ? undefined : { background: tone.background }}
                    title={title}
                    data-testid="qs-scorecard-heatmap-cell"
                    data-dim={dim.dimension}
                    data-item={col.id}
                    data-value={suppressed ? '' : cell.mean}
                    data-polarity={tone.polarity}
                    data-intensity={tone.intensity}
                    data-directional={directional ? 'true' : undefined}
                    data-suppressed={suppressed ? 'true' : undefined}
                  >
                    <span className="font-medium text-twin-chat-fg">
                      {suppressed ? '–' : cell.mean.toFixed(1)}
                    </span>
                    {directional && (
                      <span
                        className="ml-0.5 text-[10px] text-twin-chat-fg-muted"
                        title="Small base — read as directional only"
                        data-testid="qs-scorecard-heatmap-directional"
                      >
                        ~
                      </span>
                    )}
                  </td>
                )
              })}
              {isPair &&
                (() => {
                  const c0 = cellFor(dimIndex, columns[0].id)
                  const c1 = cellFor(dimIndex, columns[1].id)
                  const scorable = c0 && c1 && c0.n >= CUT_SUPPRESS_N && c1.n >= CUT_SUPPRESS_N
                  if (!scorable) {
                    return (
                      <td
                        key="__delta__"
                        className="h-10 rounded-md text-center align-middle text-twin-chat-fg-muted"
                        data-testid="qs-scorecard-heatmap-delta"
                        data-dim={dim.dimension}
                      >
                        –
                      </td>
                    )
                  }
                  const gap = c0.mean - c1.mean
                  // No fill on the Δ column — the NUMBER carries the direction: favorable-lead teal,
                  // unfavorable-lead coral (same polarity logic as the cells, fed a pseudo-mean of
                  // midpoint + gap), a near-tie muted. Keeps the column quiet next to the filled cells.
                  const mid = (c0.scaleMin + c0.scaleMax) / 2
                  const dTone = scorecardCellTone(mid + gap, c0.scaleMin, c0.scaleMax, dim.higherIsBetter)
                  const tie = Math.abs(gap) < 0.05 // a rounds-to-0.0 gap is a dead heat, never "−0.0"
                  const sign = tie ? '' : gap > 0 ? '+' : '−'
                  const deltaColor =
                    tie || dTone.polarity === 'neutral' ? undefined : dTone.polarity === 'positive' ? SC_POSITIVE : SC_NEGATIVE
                  return (
                    <td
                      key="__delta__"
                      className="h-10 text-center align-middle tabular-nums"
                      title={`${columns[0].label} − ${columns[1].label} = ${sign}${Math.abs(gap).toFixed(1)}`}
                      data-testid="qs-scorecard-heatmap-delta"
                      data-dim={dim.dimension}
                      data-delta={gap.toFixed(1)}
                      data-polarity={tie ? 'neutral' : dTone.polarity}
                    >
                      <span
                        className={cn('font-medium', deltaColor ? undefined : 'text-twin-chat-fg-muted')}
                        style={deltaColor ? { color: deltaColor } : undefined}
                      >
                        {sign}
                        {Math.abs(gap).toFixed(1)}
                      </span>
                    </td>
                  )
                })()}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  const activeResponses = (bands.find((b) => b.cutId === activeBand) ?? bands[0]).responses

  // SC: the stamped grid with no cut; with cuts, each drawn band's own stamped grid once the
  // endpoint answered for every band (a withheld cut is never drawn). Null falls back to legacy.
  const stampedBody = ((): ReactNode | null => {
    if (!stampedPlot || !drawPlot) {
      return null
    }
    if (!hasCuts) {
      return stampedPlot
    }
    if (cutPlots.status === 'loading') {
      return (
        <div data-testid="qs-scorecard-cut-plots-loading">
          <ChartSkeleton />
        </div>
      )
    }
    // A stored scorecard outside a thread keeps its legacy table; a new read says so (W7 M1).
    if (cutPlots.status === 'unavailable' && !r.plot_inputs) {
      return null
    }
    const answered = cutPlots.question
    // W7 Q10: a stamped scorecard whose cut grids failed or were refused says so, with a retry.
    if (cutPlots.status !== 'ready' || answered?.status !== 'ok') {
      return <CutPlotsError onRetry={cutPlots.retry} />
    }
    const bandPlots = answered.plots ?? {}
    // A cut band reads only its own grid: the whole run's plots are cleared first.
    const readFor = (cutId: string): Result =>
      cutId === '__all__'
        ? r
        : {
            ...r,
            heat_grid_plot: null,
            stacked_100_bars_plot: null,
            scorecard_plot: null,
            ...(bandPlots[`cut:${cutId}`] ?? {}),
          }
    const drawnBands = bands.filter((band) => !band.suppressed)
    if (!drawnBands.every((band) => band.cutId === '__all__' || drawPlot(readFor(band.cutId)) !== null)) {
      return <CutPlotsError onRetry={cutPlots.retry} />
    }
    const shown = drawnBands.find((band) => band.cutId === activeBand) ?? drawnBands[0]
    return (
      <div className="space-y-3" data-testid="qs-scorecard-cut-plots">
        <CutToggle bands={drawnBands} active={shown.cutId} onSelect={setActiveBand} />
        <SegmentSwap swapKey={shown.cutId}>{drawPlot(readFor(shown.cutId))}</SegmentSwap>
        {/* A pick under the suppress floor is never drawn: named here, as the other cards do. */}
        <SuppressedCutsNote labels={bands.filter((band) => band.suppressed).map((band) => band.label)} />
      </div>
    )
  })()

  const body = (
      <div className="space-y-3" data-testid="qs-scorecard-heatmap">
        {stampedBody ?? (
          <>
            {hasCuts ? (
              <>
                <CutToggle bands={bands} active={activeBand} onSelect={setActiveBand} />
                <SegmentSwap swapKey={activeBand}>{renderTable(recomputeCell(activeResponses))}</SegmentSwap>
              </>
            ) : (
              renderTable(pythonCell)
            )}
            <ScorecardHeatmapLegend
              scaleMin={scaleMin}
              scaleMax={scaleMax}
              higherIsBetter={legendHigher}
              minLabel={minAnchorLabel}
              maxLabel={maxAnchorLabel}
            />
          </>
        )}
      </div>
  )

  // Chat surface — bare heatmap; the outer PlotCard renders the published chrome.
  if (chrome === 'none') {
    return body
  }

  // Report/preview surface — the original internal chrome, byte-identical.
  return (
    <QsCardChrome
      construct={r.title}
      footnote={footnote}
      control={
        pickerData ? (
          <CompareSegmentsPicker groups={cutGroups} selected={selectedCuts} onChange={setSelectedCuts} maxCuts={maxCuts} />
        ) : undefined
      }
    >
      {body}
    </QsCardChrome>
  )
}
